import { ICartProduct } from '@/types';
import {
  UpdateItemInput,
  addItemWithQuantity,
  removeItemOrQuantity,
  addItem,
  updateItem,
  removeItem,
  calculateUniqueItems,
  calculateItemTotals,
  calculateTotalItems,
  calculateTotal,
} from './cart.utils';
import {
  getPBMBaseUnitPrice,
  getCatalogSalePrice,
  isInterplayersPBMItem,
  mapCartVerifyPricesBySku,
} from '@/lib/utils/pbm';
import type { CartVerifyItemResponse } from '@/types';

interface Metadata {
  [key: string]: any;
}

type Action =
  | { type: 'ADD_ITEMS_WITH_QUANTITY'; items: ICartProduct[] }
  | { type: 'ADD_ITEM_WITH_QUANTITY'; item: ICartProduct; quantity: number }
  | { type: 'REMOVE_ITEM_OR_QUANTITY'; id: ICartProduct['id']; quantity?: number }
  | { type: 'ADD_ITEM'; id: ICartProduct['id']; item: ICartProduct }
  | { type: 'UPDATE_ITEM'; id: ICartProduct['id']; item: UpdateItemInput }
  | { type: 'UPDATE_ITEM_PRICES'; items: { id: string; price: number; sale_price: number; promotional_pack_unit_price?: number; product_prices?: any[] }[] }
  | { type: 'REMOVE_ITEM'; id: ICartProduct['id'] }
  | { type: 'RESET_CART' }
  | { type: 'RESET_CART_PRESERVE_LANGUAGE'; language: string }
  | { type: 'HYDRATE_MERGED_CART'; items: ICartProduct[] }
  | { type: 'UPDATE_CART_LANGUAGE'; language: string }
  | { type: 'REMOVE_PBM_FROM_ITEM'; id: ICartProduct['id'] }
  | { type: 'APPLY_PBM_VERIFIED_PRICES'; prices: CartVerifyItemResponse[] }
  | { type: 'REVERT_PBM_COMBO_PRICES' }
  | { type: 'REVERT_INTERPLAYERS_PBM_TO_CATALOG' };

const lineNow = () => new Date().toISOString();

function bumpLineTimestamps(
  items: ICartProduct[],
  ids: ICartProduct['id'][],
): ICartProduct[] {
  if (ids.length === 0) return items;
  const idSet = new Set(ids);
  return items.map((it) =>
    idSet.has(it.id)
      ? { ...it, cart_line_updated_at: lineNow() }
      : it,
  );
}

export interface State {
  items: ICartProduct[];
  settings?: any;
  isEmpty: boolean;
  totalItems: number;
  totalUniqueItems: number;
  total: number;
  meta?: Metadata | null;
  language: string;
}

export const initialState: State = {
  items: [],
  settings: {},
  isEmpty: true,
  totalItems: 0,
  totalUniqueItems: 0,
  total: 0,
  meta: null,
  language: 'pt-BR',
};

export function cartReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_ITEMS_WITH_QUANTITY': {
      const items = bumpLineTimestamps(
        [...state.items, ...action.items],
        action.items.map((i) => i.id),
      );
      return generateFinalState(state, items);
    }
    case 'ADD_ITEM_WITH_QUANTITY': {
      const items = addItemWithQuantity(
        state.items,
        action.item,
        action.quantity,
      );

      return generateFinalState(
        state,
        bumpLineTimestamps(items, [action.item.id]),
      );
    }
    case 'REMOVE_ITEM_OR_QUANTITY': {
      const items = removeItemOrQuantity(
        state.items,
        action.id,
        action.quantity ?? 1,
      );
      return generateFinalState(state, bumpLineTimestamps(items, [action.id]));
    }
    case 'ADD_ITEM': {
      const items = addItem(state.items, action.item);
      return generateFinalState(
        state,
        bumpLineTimestamps(items, [action.item.id]),
      );
    }
    case 'REMOVE_ITEM': {
      const items = removeItem(state.items, action.id);
      return generateFinalState(state, items);
    }
    case 'UPDATE_ITEM': {
      // Protect PBM items from price updates
      const existingItem = state.items.find(item => item.id === action.id);
      const isPBMItem = existingItem?.pbm_info?.flag;
      
      // If updating a PBM item, exclude price-related fields from the update
      if (isPBMItem && (action.item.price !== undefined || action.item.sale_price !== undefined || action.item.calculated_sale_price !== undefined)) {
        const { price, sale_price, calculated_sale_price, ...safeUpdate } = action.item;
        const items = updateItem(state.items, action.id, safeUpdate);
        return generateFinalState(
          state,
          bumpLineTimestamps(items, [action.id]),
        );
      }
      
      const items = updateItem(state.items, action.id, action.item);
      return generateFinalState(
        state,
        bumpLineTimestamps(items, [action.id]),
      );
    }
    case 'UPDATE_ITEM_PRICES': {
      const items = state.items.map(item => {
        // Skip price updates for PBM items to preserve user-selected PBM prices
        const isPBMItem = item.pbm_info?.flag;
        if (isPBMItem) {
          return item;
        }

        const priceUpdate = action.items.find(
          update => update.id === item.id,
        );
        if (priceUpdate) {
          // Update all price-related fields to maintain consistency
          const updatedItem = {
            ...item,
            price: priceUpdate.price,
            sale_price: priceUpdate.sale_price,
            calculated_sale_price: priceUpdate.sale_price, // Keep calculated_sale_price in sync
          };

          // Update promotional pack unit price if provided (for product kits)
          if (priceUpdate.promotional_pack_unit_price !== undefined) {
            updatedItem.promotional_pack_unit_price = priceUpdate.promotional_pack_unit_price;
          }

          // Update product_prices array if provided (from fresh product data)
          if (priceUpdate.product_prices && Array.isArray(priceUpdate.product_prices)) {
            updatedItem.product_prices = priceUpdate.product_prices;
          } else if (updatedItem.product_prices && Array.isArray(updatedItem.product_prices)) {
            // If no fresh product_prices provided, update existing ones with new prices
            updatedItem.product_prices = updatedItem.product_prices.map(productPrice => {
              // Update the promotional pack price channel (id === 14) if promotional_pack_unit_price was updated
              if (productPrice.price_channel_id === 14 && priceUpdate.promotional_pack_unit_price !== undefined) {
                return {
                  ...productPrice,
                  price: priceUpdate.promotional_pack_unit_price,
                  sale_price: priceUpdate.promotional_pack_unit_price,
                };
              }
              // Update other price channels with regular price updates
              return {
                ...productPrice,
                price: priceUpdate.price,
                sale_price: priceUpdate.sale_price,
              };
            });
          }

          return updatedItem;
        }
        return item;
      });
      return generateFinalState(state, items);
    }
    case 'HYDRATE_MERGED_CART': {
      return generateFinalState(state, action.items);
    }
    case 'UPDATE_CART_LANGUAGE': {
      return {
        ...initialState,
        language: action.language,
      };
    }
    case 'REMOVE_PBM_FROM_ITEM': {
      const items = state.items.map(item => {
        if (item.id === action.id && item.pbm_info?.flag) {
          // Remove PBM promotion: restore original price and remove pbm_info
          const originalPrice = item.price || item.sale_price;
          return {
            ...item,
            sale_price: originalPrice,
            calculated_sale_price: originalPrice,
            pbm_info: undefined,
          };
        }
        return item;
      });
      return generateFinalState(
        state,
        bumpLineTimestamps(items, [action.id]),
      );
    }
    case 'APPLY_PBM_VERIFIED_PRICES': {
      const priceBySku = mapCartVerifyPricesBySku(action.prices);
      const touchedIds: ICartProduct['id'][] = [];

      const items = state.items.map((item) => {
        if (!isInterplayersPBMItem(item)) {
          return item;
        }

        const verifiedPrice = priceBySku.get(item.sku);
        if (verifiedPrice == null) {
          return item;
        }

        const basePrice = getPBMBaseUnitPrice(item);
        const hasComboDiscount = verifiedPrice < basePrice - 0.001;

        touchedIds.push(item.id);

        return {
          ...item,
          sale_price: verifiedPrice,
          calculated_sale_price: verifiedPrice,
          pbm_info: {
            ...item.pbm_info!,
            base_sale_price: basePrice,
            combo_applied: hasComboDiscount,
            combo_verified_price: verifiedPrice,
          },
        };
      });

      return generateFinalState(
        state,
        bumpLineTimestamps(items, touchedIds),
      );
    }
    case 'REVERT_PBM_COMBO_PRICES': {
      const touchedIds: ICartProduct['id'][] = [];
      const items = state.items.map((item) => {
        if (!item.pbm_info?.combo_applied) {
          return item;
        }

        const originalPrice = getPBMBaseUnitPrice(item);
        const {
          combo_applied: _comboApplied,
          combo_verified_price: _comboVerifiedPrice,
          base_sale_price: _baseSalePrice,
          ...restPbm
        } = item.pbm_info;

        touchedIds.push(item.id);

        return {
          ...item,
          sale_price: originalPrice,
          calculated_sale_price: originalPrice,
          pbm_info: restPbm,
        };
      });

      if (touchedIds.length === 0) {
        return state;
      }

      return generateFinalState(
        state,
        bumpLineTimestamps(items, touchedIds),
      );
    }
    case 'REVERT_INTERPLAYERS_PBM_TO_CATALOG': {
      const touchedIds: ICartProduct['id'][] = [];
      const items = state.items.map((item) => {
        if (!isInterplayersPBMItem(item)) {
          return item;
        }

        const catalogPrice = getCatalogSalePrice(item);
        touchedIds.push(item.id);

        return {
          ...item,
          sale_price: catalogPrice,
          calculated_sale_price: catalogPrice,
          pbm_info: undefined,
        };
      });

      if (touchedIds.length === 0) {
        return state;
      }

      return generateFinalState(
        state,
        bumpLineTimestamps(items, touchedIds),
      );
    }
    case 'RESET_CART':
      return initialState;
    case 'RESET_CART_PRESERVE_LANGUAGE': {
      return {
        ...initialState,
        language: action.language,
        settings: state.settings ?? {},
      };
    }
    default:
      return state;
  }
}

const generateFinalState = (
  { settings = {}, ...state }: State,
  items: ICartProduct[],
) => {
  const totalUniqueItems = calculateUniqueItems(items);
  return {
    ...state,
    settings,
    items: calculateItemTotals(items, settings),
    totalItems: calculateTotalItems(items),
    totalUniqueItems,
    total: calculateTotal(items, settings),
    isEmpty: totalUniqueItems === 0,
  };
};
